import { Box, Dialog, DialogContent, IconButton } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import {
  FONT_MONO,
  FONT_SANS,
  PillButton,
  RADII,
  SchedulerThemeScope,
  TOKENS,
} from './SchedulerDesign'

/** One product a failed run left short (`pipeline::UnmetProductDetail`). */
export type UnmetProductDetail = {
  product_code: string
  product_name: string
  base_material: string
  demand: number
  produced: number
  unmet: number
  reason_code: string
  reason: string
  kitchens: string[]
  lines: string[]
}

/** What a small diagnostic attempt found (`pipeline::FailureDiagnosis`). */
export type FailureDiagnosis = {
  horizon_hours: number
  unmet_products: UnmetProductDetail[]
  more_unmet_products: number
  notices: string[]
}

/** One failed week, or the whole consolidated solve (`week: null`). */
export type RunFailure = {
  week?: number | null
  message: string
  diagnosis?: FailureDiagnosis | null
}

/** The explainer's codes, in words a planner uses. */
const REASON_LABELS: Record<string, string> = {
  NO_KITCHENS_DEFINED: 'No kitchen route',
  NO_LINES_DEFINED: 'No line route',
  NO_VALID_RATES: 'No run rate',
  EMPTY_HORIZON: 'No time to plan in',
  FULL_HORIZON_DOWNTIME: 'Down for the whole plan',
  CALENDAR_MISALIGNED: 'Kitchen and line never free together',
  INSUFFICIENT_CAPACITY: 'Not enough line time',
  PARTIAL_SCHEDULING: 'Ran out of time',
  NOT_PICKED: 'Squeezed out by other products',
}

const reasonLabel = (code: string) =>
  REASON_LABELS[code] ??
  (code ? code.replace(/_/g, ' ').toLowerCase() : 'Unknown')

const qty = (n: number) => Math.round(n).toLocaleString()

let openDetails: ((title: string, failures: RunFailure[]) => void) | null = null

/**
 * Show a run or survey failure as a toast, with a Details button when the
 * API sent per-week failures (and a host is mounted to show them). Replaces
 * a bare `toast.error`, whose one line ("All weeks failed") said nothing
 * about which products or why.
 */
export const toastRunFailure = (
  title: string,
  message: string,
  failures?: RunFailure[] | null
) => {
  const list = failures ?? []
  const canExplain = list.length > 0 && openDetails !== null
  toast.error((t) => (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}
      component="span"
    >
      <span>{message}</span>
      {canExplain && (
        <Box
          component="button"
          type="button"
          onClick={() => {
            toast.dismiss(t.id)
            openDetails?.(title, list)
          }}
          sx={{
            alignSelf: 'flex-start',
            border: `1px solid ${TOKENS.stateFailed}`,
            background: 'transparent',
            color: TOKENS.stateFailed,
            borderRadius: RADII.md,
            fontFamily: FONT_SANS,
            fontSize: 12,
            fontWeight: 600,
            px: 1,
            py: '2px',
            cursor: 'pointer',
            '&:hover': { background: '#FBEDEB' },
          }}
        >
          Details
        </Box>
      )}
    </Box>
  ))
}

/** Plain-text copy of the details, for pasting into a message or ticket. */
const asText = (title: string, failures: RunFailure[]) =>
  [
    title,
    ...failures.flatMap((f) => [
      '',
      `${f.week ? `Week ${f.week}` : 'All selected weeks'}: ${f.message}`,
      ...(f.diagnosis?.unmet_products ?? []).map(
        (p) =>
          `  ${p.product_code} ${p.product_name}: short ${qty(p.unmet)} of ${qty(p.demand)} - ${reasonLabel(p.reason_code)}. ${p.reason}`
      ),
      ...(f.diagnosis?.more_unmet_products
        ? [`  …and ${f.diagnosis.more_unmet_products} more`]
        : []),
      ...(f.diagnosis?.notices ?? []).map((n) => `  Data: ${n}`),
    ]),
  ].join('\n')

const ProductTable = ({ products }: { products: UnmetProductDetail[] }) => (
  <Box sx={{ overflowX: 'auto' }}>
    <Box
      component="table"
      sx={{
        width: '100%',
        borderCollapse: 'collapse',
        fontSize: 12,
        '& th': {
          textAlign: 'left',
          fontSize: 10.5,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          color: TOKENS.ink400,
          background: TOKENS.panelHead,
          borderBottom: `1px solid ${TOKENS.line300}`,
          px: 1,
          py: 0.75,
          whiteSpace: 'nowrap',
        },
        '& td': {
          borderBottom: `1px solid ${TOKENS.line100}`,
          px: 1,
          py: 0.75,
          verticalAlign: 'top',
          color: TOKENS.ink900,
        },
      }}
    >
      <thead>
        <tr>
          <th>Product</th>
          <th>Short</th>
          <th>Why</th>
          <th>Route</th>
        </tr>
      </thead>
      <tbody>
        {products.map((p) => (
          <tr key={p.product_code}>
            <td>
              <Box
                sx={{
                  fontFamily: FONT_MONO,
                  fontWeight: 600,
                  color: TOKENS.blue600,
                }}
              >
                {p.product_code}
              </Box>
              <Box sx={{ color: TOKENS.ink500, fontSize: 11.5 }}>
                {p.product_name}
              </Box>
            </td>
            <td style={{ whiteSpace: 'nowrap' }}>
              <Box
                sx={{
                  fontFamily: FONT_MONO,
                  color: TOKENS.stateFailed,
                  fontWeight: 600,
                }}
              >
                {qty(p.unmet)}
              </Box>
              <Box sx={{ color: TOKENS.ink500, fontSize: 11.5 }}>
                of {qty(p.demand)}
              </Box>
            </td>
            <td>
              <Box sx={{ fontWeight: 600 }}>{reasonLabel(p.reason_code)}</Box>
              <Box sx={{ color: TOKENS.ink500, fontSize: 11.5, mt: 0.25 }}>
                {p.reason}
              </Box>
            </td>
            <td style={{ fontSize: 11.5, color: TOKENS.ink700 }}>
              <div>{p.kitchens.join(', ') || '—'}</div>
              <div style={{ color: TOKENS.ink500 }}>
                → {p.lines.join(', ') || '—'}
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </Box>
  </Box>
)

/**
 * Mounted once, next to the app's Toaster (`AppProviders`). Shows the details behind a
 * `toastRunFailure` toast: each failed week, the products the diagnostic
 * attempt left short with the reason for each, and any data warnings.
 */
export const RunFailureDetailsHost = () => {
  const [state, setState] = useState<{
    title: string
    failures: RunFailure[]
  } | null>(null)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    openDetails = (title, failures) => {
      setCopied(false)
      setState({ title, failures })
    }
    return () => {
      openDetails = null
    }
  }, [])

  const close = () => setState(null)
  const copy = () => {
    if (!state) return
    navigator.clipboard
      ?.writeText(asText(state.title, state.failures))
      .then(() => setCopied(true))
      .catch(() => setCopied(false))
  }

  return (
    <Dialog
      open={state !== null}
      onClose={close}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: RADII.md,
          border: `1px solid ${TOKENS.line200}`,
        },
      }}
    >
      {state && (
        <SchedulerThemeScope>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 2.5,
              py: 1.75,
              borderBottom: `1px solid ${TOKENS.line200}`,
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Box sx={{ fontSize: 16, fontWeight: 700 }}>{state.title}</Box>
              <Box sx={{ fontSize: 12, color: TOKENS.ink400, mt: 0.25 }}>
                What stopped it, week by week. The product list comes from a
                short trial attempt, so a product squeezed out by others may fit
                in a different order.
              </Box>
            </Box>
            <IconButton aria-label="Close" onClick={close} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <DialogContent sx={{ px: 2.5, py: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              {state.failures.map((f, i) => (
                <Box key={`${f.week ?? 'all'}-${i}`}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'baseline',
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <Box
                      sx={{
                        fontSize: 10.5,
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.06em',
                        color: TOKENS.ink400,
                      }}
                    >
                      {f.week ? `Week ${f.week}` : 'All selected weeks'}
                    </Box>
                    <Box
                      sx={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: TOKENS.stateFailed,
                      }}
                    >
                      {f.message}
                    </Box>
                  </Box>
                  {f.diagnosis && f.diagnosis.unmet_products.length > 0 && (
                    <>
                      <Box sx={{ fontSize: 12, color: TOKENS.ink500, mb: 1 }}>
                        {f.diagnosis.unmet_products.length +
                          f.diagnosis.more_unmet_products}{' '}
                        product(s) could not be fully made within{' '}
                        {f.diagnosis.horizon_hours}h.
                      </Box>
                      <ProductTable products={f.diagnosis.unmet_products} />
                      {f.diagnosis.more_unmet_products > 0 && (
                        <Box sx={{ fontSize: 12, color: TOKENS.ink400, mt: 1 }}>
                          …and {f.diagnosis.more_unmet_products} more, with
                          smaller shortfalls.
                        </Box>
                      )}
                    </>
                  )}
                  {f.diagnosis && f.diagnosis.notices.length > 0 && (
                    <Box sx={{ mt: 1.5 }}>
                      <Box
                        sx={{
                          fontSize: 10.5,
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          letterSpacing: '0.06em',
                          color: TOKENS.ink400,
                          mb: 0.5,
                        }}
                      >
                        Data warnings
                      </Box>
                      <Box
                        component="ul"
                        sx={{
                          m: 0,
                          pl: 2.5,
                          fontSize: 12,
                          color: TOKENS.ink700,
                        }}
                      >
                        {f.diagnosis.notices.map((n, j) => (
                          <li key={j}>{n}</li>
                        ))}
                      </Box>
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
          </DialogContent>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1,
              px: 2.5,
              py: 1.5,
              borderTop: `1px solid ${TOKENS.line200}`,
            }}
          >
            <PillButton kind="ghost" onClick={copy}>
              {copied ? 'Copied' : 'Copy details'}
            </PillButton>
            <PillButton kind="primary" onClick={close}>
              Close
            </PillButton>
          </Box>
        </SchedulerThemeScope>
      )}
    </Dialog>
  )
}
